import React, { useState } from 'react';
import { X, Star, Image as ImageIcon, AlertCircle, CheckCircle, Camera, Info, ChevronRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import useMarketplaceStore from '../../store/marketplaceStore';
import marketplaceAPI from '../../services/marketplace.api';

const ReviewModal = ({ 
  business, 
  product, 
  service, 
  order,
  onClose, 
  onSuccess 
}) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  
  const { createReview } = useMarketplaceStore();

  const reviewTarget = business || product || service;
  const targetType = business ? 'business' : product ? 'product' : service ? 'service' : '';

  const sections = [
    { id: 'rating', name: 'Rating', icon: '⭐' },
    { id: 'title', name: 'Title', icon: '📝' },
    { id: 'review', name: 'Review', icon: '💬' },
    { id: 'photos', name: 'Photos', icon: '📷' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setShowMobileNav(false);
    }
  };

  const MobileNavDrawer = () => (
    <>
      <div className="fixed inset-0 z-40 md:hidden" onClick={() => setShowMobileNav(false)} />
      <div className="fixed bottom-24 left-4 right-4 bg-white rounded-2xl shadow-neomorph border border-primary-100/30 overflow-hidden z-50">
        <div className="p-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">Jump to Section</h3>
            <button onClick={() => setShowMobileNav(false)} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
        </div>
        <div className="p-2">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              className="w-full flex items-center space-x-3 px-3 py-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <span className="text-lg">{section.icon}</span>
              <span className="flex-1 text-left text-sm font-medium text-gray-700">{section.name}</span>
              <ChevronRight className="h-4 w-4 text-gray-400" />
            </button>
          ))}
        </div>
      </div>
    </>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    if (!comment.trim()) {
      toast.error('Please write a review');
      return;
    }

    setIsLoading(true);
    try {
      const reviewData = {
        rating,
        title: title || undefined,
        comment: comment.trim(),
        images,
        ...(business && { businessId: business.id }),
        ...(product && { productId: product.id }),
        ...(service && { serviceId: service.id }),
        ...(order && { orderId: order.id })
      };

      const response = await marketplaceAPI.createReview(reviewData);
      
      toast.success('Review submitted successfully! It will appear after moderation.');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to submit review:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to submit review');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (files) => {
    if (images.length + files.length > 5) {
      toast.error(`Maximum 5 photos allowed. You can add ${5 - images.length} more.`);
      return;
    }

    setUploadingImages(true);
    try {
      // Simulate upload - replace with actual API call
      const uploadedUrls = await Promise.all(
        Array.from(files).map(async (file) => {
          // Create local preview first
          return URL.createObjectURL(file);
        })
      );
      setImages(prev => [...prev, ...uploadedUrls]);
      toast.success('Photos added successfully');
    } catch (error) {
      toast.error('Failed to upload photos');
      console.error('Upload error:', error);
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const renderStars = (forHover = false) => {
    const currentRating = forHover ? hoverRating : rating;
    
    return (
      <div className="flex space-x-1 sm:space-x-2 justify-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setRating(star)}
            onMouseEnter={() => setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            className="focus:outline-none transform hover:scale-110 transition-transform"
          >
            <Star
              className={`h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 ${
                star <= currentRating
                  ? 'text-yellow-400 fill-current drop-shadow-md'
                  : 'text-gray-300 hover:text-gray-400'
              } transition-colors`}
            />
          </button>
        ))}
      </div>
    );
  };

  const getRatingText = () => {
    const texts = {
      1: 'Poor',
      2: 'Fair',
      3: 'Good',
      4: 'Very Good',
      5: 'Excellent'
    };
    return texts[rating] || 'Select Rating';
  };

  if (!reviewTarget) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto">
      <div className="bg-[#E8ECF5] rounded-2xl shadow-neomorph max-w-md w-full my-4 border border-primary-100/30">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-primary-100/50 sticky top-0 bg-[#E8ECF5] z-10 rounded-t-2xl">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-1 h-6 sm:h-8 bg-gradient-to-b from-yellow-400 to-yellow-600 rounded-full mr-2 sm:mr-3" />
              <div>
                <h3 className="text-base sm:text-xl font-bold text-gray-800">Write a Review</h3>
                <p className="text-xs text-gray-500 mt-0.5 hidden sm:block">Share your experience</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-500"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>

          {/* Target Info */}
          <div className="flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 bg-white rounded-xl shadow-neomorph-inset border border-primary-100/30">
            <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-md shadow-yellow-500/30 flex items-center justify-center flex-shrink-0">
              {reviewTarget?.logoUrl || reviewTarget?.images?.[0] ? (
                <img
                  src={reviewTarget.logoUrl || reviewTarget.images[0]}
                  alt={reviewTarget.name}
                  className="h-12 w-12 sm:h-14 sm:w-14 rounded-xl object-cover"
                />
              ) : (
                <ImageIcon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="font-semibold text-gray-800 truncate">{reviewTarget?.name}</h4>
              <p className="text-xs text-gray-500 capitalize mt-0.5">{targetType}</p>
              {order && (
                <p className="text-[10px] sm:text-xs text-gray-500 mt-1 bg-gray-50 px-2 py-1 rounded-lg inline-block">
                  Order #{order.orderNumber?.slice(0, 8)}...
                </p>
              )}
            </div>
          </div>

          {/* Mobile Section Navigation Button */}
          <button
            onClick={() => setShowMobileNav(true)}
            className="md:hidden mt-3 w-full py-2 bg-white rounded-lg shadow-neomorph text-sm font-medium text-gray-700 flex items-center justify-center gap-2"
          >
            <span>Jump to Section</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 max-h-[50vh] sm:max-h-[60vh] overflow-y-auto px-1">
            {/* Rating */}
            <div id="rating" className="bg-white rounded-xl shadow-neomorph p-4 sm:p-5 border border-primary-100/30 scroll-mt-20">
              <label className="block text-sm font-medium text-gray-700 mb-3 sm:mb-4">
                Overall Rating *
              </label>
              <div className="text-center">
                {renderStars()}
                <div className="mt-3">
                  <span className="text-base sm:text-lg font-semibold text-gray-800 bg-yellow-50 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg inline-block">
                    {getRatingText()}
                  </span>
                </div>
                <div className="mt-2 flex justify-between text-[10px] sm:text-xs text-gray-500 px-2">
                  <span>Poor</span>
                  <span>Fair</span>
                  <span>Good</span>
                  <span>Very Good</span>
                  <span>Excellent</span>
                </div>
              </div>
            </div>

            {/* Title */}
            <div id="title" className="bg-white rounded-xl shadow-neomorph p-4 sm:p-5 border border-primary-100/30 scroll-mt-20">
              <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Review Title (Optional)
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Summarize your experience"
                className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-yellow-500/20 text-sm"
                maxLength={100}
              />
              <div className="mt-1.5 sm:mt-2 text-right text-[10px] sm:text-xs text-gray-500">
                {title.length}/100
              </div>
            </div>

            {/* Comment */}
            <div id="review" className="bg-white rounded-xl shadow-neomorph p-4 sm:p-5 border border-primary-100/30 scroll-mt-20">
              <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Your Review *
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Tell us about your experience. What did you like or dislike?"
                rows={5}
                className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-yellow-500/20 resize-none text-sm"
                maxLength={1000}
              />
              <div className="mt-1.5 sm:mt-2 flex flex-col sm:flex-row sm:justify-between gap-1 text-[10px] sm:text-xs">
                <span className="text-gray-500 bg-gray-50 px-2 py-1 rounded-lg">
                  Detailed reviews help others
                </span>
                <span className="text-gray-500 bg-gray-50 px-2 py-1 rounded-lg">
                  {comment.length}/1000
                </span>
              </div>
            </div>
          </div>

          {/* Submit Button - Added padding bottom for mobile nav */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4 pb-6 sm:pb-4 px-4 sm:px-6 border-t border-primary-100/50 sticky bottom-0 bg-[#E8ECF5] rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              className="order-2 sm:order-1 w-full sm:flex-1 px-4 py-2 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || rating === 0 || !comment.trim()}
              className="order-1 sm:order-2 w-full sm:flex-1 px-4 py-2 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-medium rounded-lg shadow-md shadow-yellow-500/30 hover:shadow-lg transition-all flex items-center justify-center disabled:opacity-50 text-sm"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Submit Review
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {showMobileNav && <MobileNavDrawer />}

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
        .scroll-mt-20 {
          scroll-margin-top: 100px;
        }
      `}</style>
    </div>
  );
};

export default ReviewModal;